import { Injectable, computed, signal } from '@angular/core';

const TOKEN_KEY = 'mm-booking-guest-jwt';
const EMAIL_KEY = 'mm-booking-guest-email';
const CLIENT_KEY = 'mm-booking-guest-registered-client-id';
const FIRST_NAME_KEY = 'mm-booking-guest-first-name';
const LAST_NAME_KEY = 'mm-booking-guest-last-name';

export interface GuestSessionProfile {
  firstName?: string;
  lastName?: string;
}

@Injectable({ providedIn: 'root' })
export class TokenStorage {
  private readonly token = signal<string | null>(this.read(TOKEN_KEY));
  private readonly email = signal<string | null>(this.read(EMAIL_KEY));
  private readonly clientIdStore = signal<string | null>(this.read(CLIENT_KEY));
  private readonly firstName = signal<string | null>(this.read(FIRST_NAME_KEY));
  private readonly lastName = signal<string | null>(this.read(LAST_NAME_KEY));

  readonly accessToken = computed(() => this.token());
  readonly userEmail = computed(() => this.email());
  readonly userFirstName = computed(() => this.firstName());
  readonly userLastName = computed(() => this.lastName());
  /** Client (marina operator) UUID from the last successful guest login — used when QR context is absent. */
  readonly registeredClientId = computed(() => this.clientIdStore());

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setSession(
    token: string,
    email: string,
    clientId?: string | null,
    profile?: GuestSessionProfile | null,
  ): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, email);
    this.token.set(token);
    this.email.set(email);
    if (clientId?.trim()) {
      const id = clientId.trim();
      localStorage.setItem(CLIENT_KEY, id);
      this.clientIdStore.set(id);
    }
    if (profile?.firstName?.trim()) {
      const v = profile.firstName.trim();
      localStorage.setItem(FIRST_NAME_KEY, v);
      this.firstName.set(v);
    }
    if (profile?.lastName?.trim()) {
      const v = profile.lastName.trim();
      localStorage.setItem(LAST_NAME_KEY, v);
      this.lastName.set(v);
    }
  }

  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(CLIENT_KEY);
    localStorage.removeItem(FIRST_NAME_KEY);
    localStorage.removeItem(LAST_NAME_KEY);
    this.token.set(null);
    this.email.set(null);
    this.clientIdStore.set(null);
    this.firstName.set(null);
    this.lastName.set(null);
  }
}
